import { useMemo } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { WidgetContainer } from '@pulse-web-ui/containers';
import { Spinner } from '@pulse-web-ui/spinner';
import { breakpoints, typography } from '@pulse-web-ui/theme';
import { observer } from 'mobx-react-lite';

import { BackButton, PROFILE, useIsDesktop, useStores } from '@shared/index';

import {
  type AgentProfileHeaderProps,
  submitProfileEditSchema,
} from '../../model';
import { getContacts } from '../../utils';
import { AddressSection } from '../address-section';
import { BankDetailsSection } from '../bank-details-section';
import { ContactsSection } from '../contacts-section';
import { CreatedDocumentsSection } from '../created-documents-section';
import { PersonalDataSection } from '../personal-data-section';
import { AttachDocuments, SubmitProfileEdit } from '../submit-profile-edit';
import { AgentProfileContainer } from './agent-profile-container';
import { DescriptionText, SectionsWrapper } from './agent-profile.styles';

export const AgentProfileSubmitEdit = observer(() => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop(breakpoints.DESKTOP);
  const navigate = useNavigate();
  const {
    MainStore: {
      agentContractStore: { profile, agentProfileDraft, filesToUpdateProfile },
    },
  } = useStores();

  const handleGoBack = () => {
    navigate(PROFILE);
  };

  if (!agentProfileDraft) {
    return <Spinner />;
  }

  const contacts = getContacts(agentProfileDraft);
  const contactsToCompare = getContacts(profile);

  const methods = useForm({
    resolver: yupResolver(submitProfileEditSchema),
    mode: 'onBlur',
    shouldFocusError: true,
    context: { isFileRequired: !!filesToUpdateProfile?.length },
  });

  const headerProps = useMemo<AgentProfileHeaderProps>(
    () => ({
      fontSize: isDesktop ? typography.medium20 : typography.medium18,
      marginBottom: 16,
    }),
    [isDesktop]
  );

  return (
    <WidgetContainer>
      <BackButton
        goBack={handleGoBack}
        testId='AgentProfileSubmitEditBackButton'
      />
      <AgentProfileContainer
        label={t('AGENT_PROFILE:headers.checkAndSubmit')}
        isWithHeaderPadding={false}
      >
        <DescriptionText>
          {t('AGENT_PROFILE:hints.checkAndSubmitHint')}
        </DescriptionText>
        <SectionsWrapper>
          <PersonalDataSection
            isSubmitSection
            profile={agentProfileDraft}
            compareProfile={profile}
            headerProps={headerProps}
          />
          <ContactsSection
            isRenderTitle
            isSubmitSection
            contacts={contacts}
            contactsToCompare={contactsToCompare}
            headerProps={headerProps}
          />
          <AddressSection
            isRenderTitle
            isSubmitSection
            addresses={agentProfileDraft?.addressesAgents}
            addressesToCompare={profile?.addressesAgents}
            headerProps={headerProps}
          />
          <BankDetailsSection
            isRenderTitle
            isSubmitSection
            bankDetails={agentProfileDraft?.bankDetails}
            bankDetailsToCompare={profile?.bankDetails}
            headerProps={headerProps}
          />

          <CreatedDocumentsSection headerProps={headerProps} />
          <FormProvider {...methods}>
            {!!filesToUpdateProfile?.length && (
              <AttachDocuments documents={filesToUpdateProfile?.join(', ')} />
            )}
            <SubmitProfileEdit />
          </FormProvider>
        </SectionsWrapper>
      </AgentProfileContainer>
    </WidgetContainer>
  );
});
